/**
 * Debug panel: edits every numeric leaf of the working BalanceConfig live.
 * Changes apply to the NEXT fight (the sim gets a snapshot at fight start).
 */
import type { BalanceConfig } from '../config/balance'

type AnyObj = Record<string, unknown>

function getPath(obj: AnyObj, path: string[]): unknown {
  let cur: unknown = obj
  for (const k of path) {
    if (cur == null || typeof cur !== 'object') return undefined
    cur = (cur as AnyObj)[k]
  }
  return cur
}

function setPath(obj: AnyObj, path: string[], value: number): void {
  let cur: AnyObj = obj
  for (let i = 0; i < path.length - 1; i++) cur = cur[path[i]!] as AnyObj
  cur[path[path.length - 1]!] = value
}

function collectNumericLeaves(obj: unknown, prefix: string[] = []): string[][] {
  const out: string[][] = []
  if (obj == null || typeof obj !== 'object') return out
  for (const [k, v] of Object.entries(obj as AnyObj)) {
    if (typeof v === 'number') out.push([...prefix, k])
    else if (typeof v === 'object' && v !== null) out.push(...collectNumericLeaves(v, [...prefix, k]))
  }
  return out
}

export function buildDebugPanel(
  container: HTMLElement,
  working: BalanceConfig,
  defaults: BalanceConfig,
): void {
  container.innerHTML = ''
  container.classList.add('debug')

  const topRow = document.createElement('div')
  topRow.className = 'row'
  const resetAll = document.createElement('button')
  resetAll.textContent = 'Alles auf Default'
  resetAll.addEventListener('click', () => {
    const fresh = JSON.parse(JSON.stringify(defaults)) as AnyObj
    for (const key of Object.keys(fresh)) {
      ;(working as unknown as AnyObj)[key] = fresh[key]
    }
    buildDebugPanel(container, working, defaults)
  })
  topRow.appendChild(resetAll)
  const hint = document.createElement('span')
  hint.className = 'hint'
  hint.textContent = 'wirkt ab dem nächsten Kampf'
  topRow.appendChild(hint)
  container.appendChild(topRow)

  const groups = Object.keys(working) as Array<keyof BalanceConfig>
  for (const group of groups) {
    const det = document.createElement('details')
    const sum = document.createElement('summary')
    const title = document.createElement('span')
    title.textContent = group
    sum.appendChild(title)
    const resetBtn = document.createElement('button')
    resetBtn.textContent = 'Reset'
    resetBtn.addEventListener('click', (ev) => {
      ev.preventDefault()
      ev.stopPropagation()
      ;(working as unknown as AnyObj)[group] = JSON.parse(JSON.stringify((defaults as unknown as AnyObj)[group]))
      buildDebugPanel(container, working, defaults)
    })
    sum.appendChild(resetBtn)
    det.appendChild(sum)

    const leaves = collectNumericLeaves((working as unknown as AnyObj)[group], [group])
    for (const path of leaves) {
      const row = document.createElement('div')
      row.className = 'cfg-row'
      const label = document.createElement('label')
      label.textContent = path.slice(1).join('.')
      label.title = path.join('.')
      const input = document.createElement('input')
      input.type = 'number'
      const cur = getPath(working as unknown as AnyObj, path)
      const def = getPath(defaults as unknown as AnyObj, path)
      input.value = String(cur)
      input.title = `Default: ${String(def)}`
      if (cur !== def) input.classList.add('changed')
      input.addEventListener('change', () => {
        const v = Math.round(Number(input.value))
        if (!Number.isFinite(v)) {
          input.value = String(getPath(working as unknown as AnyObj, path))
          return
        }
        input.value = String(v)
        setPath(working as unknown as AnyObj, path, v)
        input.classList.toggle('changed', v !== def)
      })
      row.appendChild(label)
      row.appendChild(input)
      det.appendChild(row)
    }
    container.appendChild(det)
  }
}
